import { Box, Card, CardActionArea, CardContent, Stack, Typography, TypographyProps } from '@mui/material';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import LinkComponent from '@/components/Link';
import { Link } from '@/i18n/navigation';
import { Image as ImageType } from '@/types/image.type';
import { Link as LinkType } from '@/types/link.type';

import styles from './ProjectCard.module.scss';

interface ProjectCardProps {
  image: ImageType;
  imageMobile?: ImageType;
  title: string;
  description: string;
  titleComponent?: TypographyProps['component'];
  link?: LinkType;
}

const ProjectCard = ({ image, imageMobile, title, description, link, titleComponent = 'h3' }: ProjectCardProps) => {
  const t = useTranslations('common');
  // @ts-expect-error - Dynamic translation key from config
  const projectTitle = t(title);
  const ariaLabel = link ? `${t('readMoreAbout')}: ${projectTitle}` : undefined;

  return (
    <Card classes={{ root: styles.root }} className={styles.container}>
      <CardActionArea>
        {link && <Link href={link?.href as '/'} target={link?.target} aria-label={ariaLabel} className={styles.link} />}
        <Box className={imageMobile ? styles.imageWrapperDesktop : styles.imageWrapper}>
          <Image src={image.src} alt={image.alt} fill sizes="(max-width: 600px) 100vw, 50vw" className={styles.image} />
        </Box>
        {imageMobile && (
          <Box className={styles.imageWrapperMobile}>
            <Image src={imageMobile.src} alt={imageMobile.alt} fill sizes="100vw" className={styles.image} />
          </Box>
        )}
        <CardContent className={styles.content}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component={titleComponent}>
              {projectTitle}
            </Typography>
            {link && (
              <LinkComponent
                href={link.href as '/'}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                text={`${t(link.text as any)} ${projectTitle}`}
                target={link.target}
                ariaLabel={ariaLabel}
                className={styles.linkText}
                tabIndex={-1}
                aria-hidden
              />
            )}
          </Stack>
          <Typography variant="body1" className={styles.description}>
            {/* @ts-expect-error - Dynamic translation key from config */}
            {t(description)}
          </Typography>
          {link && (
            <LinkComponent
              href={link.href as '/'}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              text={`${t(link.text as any)} ${projectTitle}`}
              target={link.target}
              ariaLabel={ariaLabel}
              className={styles.linkTextMobile}
              tabIndex={-1}
              aria-hidden
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ProjectCard;
